// client/src/components/AdventureSessionBanner.tsx
// Minimal visual indicator that an adventure session is active.
// Surfaces the adventure title, genre, character name, and scene count so
// the player always knows the game is running in the background. Polls
// /api/adventure/active; shows nothing when no session is active.

import React, { useEffect, useState } from 'react';
import { getStoredToken } from '../contexts/AuthContext';
import '../assets/styles/AdventureSessionBanner.css';

interface ActiveAdventure {
  id: string;
  title: string;
  genre: string[];
  tone: string;
  sceneCount: number;
  characterName?: string;
  characterState?: string;
  currentSceneId: string | null;
  openThreads: string[];
}

interface AdventureStatusResponse {
  active: boolean;
  adventure?: ActiveAdventure;
}

export const AdventureSessionBanner: React.FC = () => {
  const [adventure, setAdventure] = useState<ActiveAdventure | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchActive = async () => {
      try {
        const token = getStoredToken();
        const res = await fetch('/api/adventure/active', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!res.ok) return;
        const data: AdventureStatusResponse = await res.json();
        if (cancelled) return;
        if (data.active && data.adventure) {
          setAdventure(data.adventure);
        } else {
          setAdventure(null);
        }
      } catch {
        // network blips are fine — next poll will recover
      }
    };

    fetchActive();
    const interval = setInterval(fetchActive, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (!adventure) return null;

  return (
    <div className="adventure-session-banner" role="status" aria-label="Adventure session active">
      <div className="adventure-banner-glyph">🎲</div>
      <div className="adventure-banner-main">
        <div className="adventure-banner-title">
          <span className="adventure-label">Adventure</span>
          <span className="adventure-title-text">{adventure.title}</span>
        </div>
        <div className="adventure-banner-meta">
          {adventure.genre.length > 0 && (
            <span className="adventure-meta-item">
              {adventure.genre.join(' · ')}
            </span>
          )}
          {adventure.tone && (
            <span className="adventure-meta-item adventure-meta-tone">
              {adventure.tone}
            </span>
          )}
          {adventure.characterName && (
            <span className="adventure-meta-item adventure-meta-character">
              playing <strong>{adventure.characterName}</strong>
              {adventure.characterState && (
                <span className="adventure-state"> · {adventure.characterState}</span>
              )}
            </span>
          )}
          <span className="adventure-meta-item adventure-meta-scenes">
            scene {adventure.sceneCount}
          </span>
          {adventure.openThreads.length > 0 && (
            <span className="adventure-meta-item adventure-meta-threads">
              {adventure.openThreads.length} open thread{adventure.openThreads.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdventureSessionBanner;
